// Pure-JS indicator computations for the chart.
//
// Engine-decoupled per CE-04a scope rule §8: SMA is computed in JS on the
// candle array (no engine round-trip). When EI-02+ lands and the engine's
// `compute_all_indicators` exposes SMA over the bridge, the chart can
// switch to engine output without changing the function signature.

const HEX6_RE = /^#[0-9a-fA-F]{6}$/

/**
 * Translate `style.ma.opacity` (0-100) into an alpha-channel hex suffix on a
 * 6-digit hex color, producing an 8-digit `#RRGGBBAA` string accepted by
 * Lightweight Charts v4 line series `color` option.
 *
 * Pure function. Defensive: clamps opacity to [0, 100], non-finite opacity
 * resolves to fully opaque (alpha=ff), and any non-`#RRGGBB` input returns
 * unchanged so callers can pass through the engine's invalid color strings
 * without breaking the chart.
 *
 * @param {string} hex          6-digit hex color string (e.g. `#2962FF`).
 * @param {number} opacityPct   0..100; values outside the range clamp.
 * @returns {string}            8-digit hex `#rrggbbaa` lowercase, or input unchanged on bad hex.
 */
export function applyOpacityToColor(hex, opacityPct) {
  if (typeof hex !== 'string' || !HEX6_RE.test(hex)) return hex
  const pct = Number.isFinite(opacityPct)
    ? Math.max(0, Math.min(100, opacityPct))
    : 100
  const alpha = Math.round((pct / 100) * 255)
  const alphaHex = alpha.toString(16).padStart(2, '0')
  return `${hex.toLowerCase()}${alphaHex}`
}

/**
 * Allowed source enum values for SMA. Mirrors indicator-settings-spec.md §1.
 * @type {ReadonlyArray<'open'|'high'|'low'|'close'|'hl2'|'hlc3'|'ohlc4'|'hlcc4'>}
 */
export const SMA_SOURCE_VALUES = Object.freeze([
  'open', 'high', 'low', 'close', 'hl2', 'hlc3', 'ohlc4', 'hlcc4',
])

export const KELTNER_BANDS_STYLE_VALUES = Object.freeze([
  'average_true_range', 'true_range', 'range',
])

function pickSource(bar, source) {
  switch (source) {
    case 'open':  return bar.open
    case 'high':  return bar.high
    case 'low':   return bar.low
    case 'close': return bar.close
    case 'hl2':   return (bar.high + bar.low) / 2
    case 'hlc3':  return (bar.high + bar.low + bar.close) / 3
    case 'ohlc4': return (bar.open + bar.high + bar.low + bar.close) / 4
    case 'hlcc4': return (bar.high + bar.low + bar.close + bar.close) / 4
    default:      return bar.close
  }
}

/**
 * Simple Moving Average over the candle array. Pure function.
 *
 * Returns one `{ time, value }` point per bar where the rolling window of
 * length `length` is fully populated AND the offset-shifted target bar
 * exists. Bars before the first complete window (index < length - 1) and
 * windows that contain a non-finite source value are skipped (no sparse
 * `null` entries — matches the canonical TradeChart `calcSMA` convention.
 *
 * Sign convention for `offset`:
 *   - `+1` shifts the SMA line FORWARD in time (one bar to the right).
 *   - `-1` shifts the SMA line BACKWARD in time (one bar to the left).
 * TA libraries vary on this; the convention here is locked unambiguously
 * for forward-compatibility with EI-02 (Pine `offset=` parameter shipped).
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number, volume:number}>} bars
 * @param {number} length    Window size, integer ≥ 1.
 * @param {'open'|'high'|'low'|'close'|'hl2'|'hlc3'|'ohlc4'|'hlcc4'} [source='close']
 * @param {number} [offset=0]   Integer shift in bars; positive = forward.
 * @returns {Array<{time:number, value:number}>}
 */
export function computeSMA(bars, length, source = 'close', offset = 0) {
  if (!Array.isArray(bars) || bars.length === 0) return []
  if (!Number.isFinite(length) || length <= 0) return []
  const len = Math.floor(length)
  if (len > bars.length) return []
  const off = Number.isFinite(offset) ? Math.floor(offset) : 0

  const out = []
  for (let i = len - 1; i < bars.length; i++) {
    let sum = 0
    let valid = true
    for (let j = i - len + 1; j <= i; j++) {
      const v = pickSource(bars[j], source)
      if (!Number.isFinite(v)) { valid = false; break }
      sum += v
    }
    if (!valid) continue

    const targetIndex = i + off
    if (targetIndex < 0 || targetIndex >= bars.length) continue
    const t = bars[targetIndex].time
    if (!Number.isFinite(t)) continue
    out.push({ time: t, value: sum / len })
  }
  return out
}

/**
 * Exponential Moving Average over the candle array. Pure function.
 *
 * Pine Script / TradingView convention (locked per CE-04b scope rule §28):
 *   - Multiplier: `alpha = 2 / (length + 1)`.
 *   - Recurrence: `EMA(t) = alpha × source(t) + (1 - alpha) × EMA(t-1)`.
 *   - Seed:       `EMA(length-1) = SMA of source[0..length-1]`.
 *
 * The Wilder/TV seed (SMA of first `length` values) is chosen rather than the
 * `EMA(0) = source(0)` convention found in some TA libraries — that produces
 * different early values and would diverge from TradingView's overlay.
 *
 * Returns one `{ time, value }` per bar from index `length-1` onward where the
 * seed window is fully populated AND the offset-shifted target bar exists.
 * Same sparse-vs-dropped convention as `computeSMA`: bars before the first
 * complete window are skipped (no `null` entries), and bars whose source is
 * non-finite are skipped while the recurrence resumes from the prior valid
 * EMA on the next finite bar (if the seed already formed). If the seed
 * window itself contains a non-finite value, the EMA cannot start and the
 * function returns `[]`.
 *
 * Sign convention for `offset` matches `computeSMA`:
 *   - `+1` shifts the EMA line FORWARD in time.
 *   - `-1` shifts the EMA line BACKWARD in time.
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number, volume:number}>} bars
 * @param {number} length    Window size, integer ≥ 1.
 * @param {'open'|'high'|'low'|'close'|'hl2'|'hlc3'|'ohlc4'|'hlcc4'} [source='close']
 * @param {number} [offset=0]   Integer shift in bars; positive = forward.
 * @returns {Array<{time:number, value:number}>}
 */
export function computeEMA(bars, length, source = 'close', offset = 0) {
  if (!Array.isArray(bars) || bars.length === 0) return []
  if (!Number.isFinite(length) || length <= 0) return []
  const len = Math.floor(length)
  if (len > bars.length) return []
  const off = Number.isFinite(offset) ? Math.floor(offset) : 0

  // Seed = SMA of the first `len` source values. If any seed-window value is
  // non-finite, the EMA cannot start (no clean way to reseed mid-history),
  // so return [].
  let seedSum = 0
  for (let i = 0; i < len; i++) {
    const v = pickSource(bars[i], source)
    if (!Number.isFinite(v)) return []
    seedSum += v
  }
  const alpha = 2 / (len + 1)
  let prev = seedSum / len

  const out = []
  function emit(i, value) {
    const targetIndex = i + off
    if (targetIndex < 0 || targetIndex >= bars.length) return
    const t = bars[targetIndex].time
    if (!Number.isFinite(t)) return
    out.push({ time: t, value })
  }

  // Emit the seed point.
  emit(len - 1, prev)

  // Recurrence over the remaining bars.
  for (let i = len; i < bars.length; i++) {
    const v = pickSource(bars[i], source)
    if (!Number.isFinite(v)) continue
    const ema = alpha * v + (1 - alpha) * prev
    prev = ema
    emit(i, ema)
  }
  return out
}

function computeSourceMovingAverage(bars, length, source, useEma) {
  const len = Math.floor(length)
  const out = new Array(bars.length).fill(NaN)
  const values = bars.map((bar) => pickSource(bar, source))
  if (len <= 0 || len > bars.length) return out

  if (!useEma) {
    for (let i = len - 1; i < bars.length; i++) {
      let sum = 0
      let valid = true
      for (let j = i - len + 1; j <= i; j++) {
        const v = values[j]
        if (!Number.isFinite(v)) { valid = false; break }
        sum += v
      }
      if (valid) out[i] = sum / len
    }
    return out
  }

  let seedSum = 0
  for (let i = 0; i < len; i++) {
    const v = values[i]
    if (!Number.isFinite(v)) return out
    seedSum += v
  }
  const alpha = 2 / (len + 1)
  let prev = seedSum / len
  out[len - 1] = prev
  for (let i = len; i < bars.length; i++) {
    const v = values[i]
    if (!Number.isFinite(v)) continue
    prev = alpha * v + (1 - alpha) * prev
    out[i] = prev
  }
  return out
}

function trueRangeAt(bars, i) {
  const bar = bars[i]
  const high = bar?.high
  const low = bar?.low
  if (!Number.isFinite(high) || !Number.isFinite(low)) return NaN
  if (i === 0) return high - low
  const prevClose = bars[i - 1]?.close
  if (!Number.isFinite(prevClose)) return NaN
  return Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose))
}

function rollingAverage(values, length) {
  const len = Math.floor(length)
  const out = new Array(values.length).fill(NaN)
  if (len <= 0 || len > values.length) return out
  for (let i = len - 1; i < values.length; i++) {
    let sum = 0
    let valid = true
    for (let j = i - len + 1; j <= i; j++) {
      const v = values[j]
      if (!Number.isFinite(v)) { valid = false; break }
      sum += v
    }
    if (valid) out[i] = sum / len
  }
  return out
}

/**
 * Keltner Channels over the candle array. Pure function.
 *
 * Mirrors the chart demo's TradingView-style KC settings:
 *   - `length` controls the middle moving average.
 *   - `source` controls the middle source series.
 *   - `use_exponential_ma=true` uses EMA; false uses SMA.
 *   - `bands_style` selects average true range, raw true range, or high-low
 *     range as the envelope distance.
 *   - `atr_length` controls the rolling average window when the band style
 *     uses an average.
 *
 * Returns one `{ time, basis, upper, lower }` point per bar where both the
 * middle line and band distance are populated.
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number, volume:number}>} bars
 * @param {number} length
 * @param {number} multiplier
 * @param {'open'|'high'|'low'|'close'|'hl2'|'hlc3'|'ohlc4'|'hlcc4'} [source='ohlc4']
 * @param {boolean} [use_exponential_ma=true]
 * @param {'average_true_range'|'true_range'|'range'} [bands_style='average_true_range']
 * @param {number} [atr_length=10]
 * @returns {Array<{time:number, basis:number, upper:number, lower:number}>}
 */
export function computeKeltner(
  bars,
  length,
  multiplier,
  source = 'ohlc4',
  use_exponential_ma = true,
  bands_style = 'average_true_range',
  atr_length = 10
) {
  if (!Array.isArray(bars) || bars.length === 0) return []
  if (!Number.isFinite(length) || length <= 0) return []
  if (!Number.isFinite(multiplier) || multiplier < 0) return []
  if (!Number.isFinite(atr_length) || atr_length <= 0) return []
  const len = Math.floor(length)
  const atrLen = Math.floor(atr_length)
  if (len > bars.length || atrLen > bars.length) return []
  if (!KELTNER_BANDS_STYLE_VALUES.includes(bands_style)) return []

  const basis = computeSourceMovingAverage(bars, len, source, use_exponential_ma !== false)
  const tr = bars.map((_, i) => trueRangeAt(bars, i))
  const ranges = bars.map((bar) => {
    const h = bar?.high
    const l = bar?.low
    return Number.isFinite(h) && Number.isFinite(l) ? h - l : NaN
  })
  let bandDistance
  if (bands_style === 'true_range') {
    bandDistance = tr
  } else if (bands_style === 'range') {
    bandDistance = rollingAverage(ranges, atrLen)
  } else {
    bandDistance = rollingAverage(tr, atrLen)
  }

  const out = []
  for (let i = 0; i < bars.length; i++) {
    const b = basis[i]
    const distance = bandDistance[i]
    const t = bars[i]?.time
    if (!Number.isFinite(t) || !Number.isFinite(b) || !Number.isFinite(distance)) continue
    const offBy = multiplier * distance
    out.push({ time: t, basis: b, upper: b + offBy, lower: b - offBy })
  }
  return out
}

/**
 * Bollinger Bands over the candle array. Pure function.
 *
 * Population-stdev convention (TradingView-locked per CE-04c rule §33):
 *   - basis(i) = SMA of source over the rolling window of size `length`.
 *   - stdev(i) = sqrt( sum_j (x_j - basis(i))^2 / length ) — divisor is
 *     `length`, NOT `length - 1`. Sample-stdev (Bessel-corrected) would
 *     diverge from TradingView's overlay and is rejected here.
 *   - upper(i) = basis(i) + std_mult * stdev(i).
 *   - lower(i) = basis(i) - std_mult * stdev(i).
 *
 * Returns one `{ time, basis, upper, lower }` point per bar where the
 * rolling window is fully populated AND the offset-shifted target bar
 * exists. Same sparse-vs-dropped convention as `computeSMA`: bars whose
 * window contains any non-finite source value are skipped, no `null`
 * entries.
 *
 * Sign convention for `offset` matches `computeSMA`/`computeEMA`.
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number, volume:number}>} bars
 * @param {number} length    Window size, integer ≥ 1.
 * @param {number} std_mult  Standard-deviation multiplier (≥ 0).
 * @param {'open'|'high'|'low'|'close'|'hl2'|'hlc3'|'ohlc4'|'hlcc4'} [source='close']
 * @param {number} [offset=0]   Integer shift in bars; positive = forward.
 * @returns {Array<{time:number, basis:number, upper:number, lower:number}>}
 */
export function computeBollinger(bars, length, std_mult, source = 'close', offset = 0) {
  if (!Array.isArray(bars) || bars.length === 0) return []
  if (!Number.isFinite(length) || length <= 0) return []
  const len = Math.floor(length)
  if (len > bars.length) return []
  if (!Number.isFinite(std_mult) || std_mult < 0) return []
  const off = Number.isFinite(offset) ? Math.floor(offset) : 0

  const out = []
  for (let i = len - 1; i < bars.length; i++) {
    let sum = 0
    let valid = true
    const window = new Array(len)
    for (let j = 0; j < len; j++) {
      const v = pickSource(bars[i - len + 1 + j], source)
      if (!Number.isFinite(v)) { valid = false; break }
      window[j] = v
      sum += v
    }
    if (!valid) continue
    const basis = sum / len
    let varSum = 0
    for (let j = 0; j < len; j++) {
      const d = window[j] - basis
      varSum += d * d
    }
    const stdev = Math.sqrt(varSum / len)
    const offBy = std_mult * stdev

    const targetIndex = i + off
    if (targetIndex < 0 || targetIndex >= bars.length) continue
    const t = bars[targetIndex].time
    if (!Number.isFinite(t)) continue
    out.push({ time: t, basis, upper: basis + offBy, lower: basis - offBy })
  }
  return out
}

/**
 * Donchian Channels over the candle array. Pure function.
 *
 * No `source` parameter — the math walks `bar.high` / `bar.low` directly:
 *   - upper(i) = max(bar.high for bar in window of size `length`).
 *   - lower(i) = min(bar.low  for bar in window of size `length`).
 *   - basis(i) = (upper(i) + lower(i)) / 2.
 *
 * Returns one `{ time, upper, basis, lower }` point per bar where the
 * rolling window is fully populated AND the offset-shifted target bar
 * exists. Bars whose window contains any non-finite high/low are skipped.
 *
 * Sign convention for `offset` matches `computeSMA`/`computeEMA`.
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number, volume:number}>} bars
 * @param {number} length    Window size, integer ≥ 1.
 * @param {number} [offset=0]   Integer shift in bars; positive = forward.
 * @returns {Array<{time:number, upper:number, basis:number, lower:number}>}
 */
/**
 * Allowed coloring modes for `computeVolumeBarColors`. Mirrors
 * indicator-settings-spec.md §5: `'vs_open'` colors each bar by close-vs-
 * current-open (TradingView factory default); `'vs_prev_close'` colors each
 * bar by close-vs-previous-close.
 * @type {ReadonlyArray<'vs_open'|'vs_prev_close'>}
 */
export const VOLUME_COLOR_MODES = Object.freeze(['vs_open', 'vs_prev_close'])

/**
 * Per-bar color array for the Volume histogram. Pure function.
 *
 * Returns one `{ time, value, color }` entry per input bar (no warm-up
 * window, no offset, no length). LWC v4 `addHistogramSeries` accepts this
 * shape directly for per-bar coloring.
 *
 * Coloring modes:
 *   - `'vs_open'`        — `color = bar.close >= bar.open ? growingColor : fallingColor`
 *     per bar. TradingView factory default per indicator-settings-spec.md §5.
 *   - `'vs_prev_close'`  — `color = bar.close >= prevBar.close ? growingColor : fallingColor`
 *     per bar. The first bar has no previous close and falls back to
 *     `growingColor`.
 *
 * The `value` field is `bar.volume` passthrough (no scaling, no transform).
 * `time` is `bar.time` passthrough. Non-finite `bar.volume` is emitted as-is
 * — downstream LWC handles malformed values per its own contract.
 *
 * Throws on invalid `mode` (not in `VOLUME_COLOR_MODES`) and on non-string
 * color arguments. Hex-format validation is NOT this function's concern —
 * downstream `applyOpacityToColor` already guards bad hex; here we accept
 * any string and emit it verbatim.
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number, volume:number}>} bars
 * @param {'vs_open'|'vs_prev_close'} mode
 * @param {string} growingColor   Hex color (e.g. `#089981`) for up bars.
 * @param {string} fallingColor   Hex color (e.g. `#F23645`) for down bars.
 * @returns {Array<{time:number, value:number, color:string}>}
 */
export function computeVolumeBarColors(bars, mode, growingColor, fallingColor) {
  if (mode !== 'vs_open' && mode !== 'vs_prev_close') {
    throw new Error(
      `computeVolumeBarColors: invalid mode '${String(mode)}'; expected 'vs_open' or 'vs_prev_close'.`
    )
  }
  if (typeof growingColor !== 'string' || typeof fallingColor !== 'string') {
    throw new Error('computeVolumeBarColors: growingColor and fallingColor must be strings.')
  }
  if (!Array.isArray(bars) || bars.length === 0) return []

  const out = new Array(bars.length)
  for (let i = 0; i < bars.length; i++) {
    const bar = bars[i]
    let color
    if (mode === 'vs_open') {
      color = bar.close >= bar.open ? growingColor : fallingColor
    } else {
      if (i === 0) {
        // No previous bar — fall back to growing color.
        color = growingColor
      } else {
        color = bar.close >= bars[i - 1].close ? growingColor : fallingColor
      }
    }
    out[i] = { time: bar.time, value: bar.volume, color }
  }
  return out
}

export function computeDonchian(bars, length, offset = 0) {
  if (!Array.isArray(bars) || bars.length === 0) return []
  if (!Number.isFinite(length) || length <= 0) return []
  const len = Math.floor(length)
  if (len > bars.length) return []
  const off = Number.isFinite(offset) ? Math.floor(offset) : 0

  const out = []
  for (let i = len - 1; i < bars.length; i++) {
    let hi = -Infinity
    let lo = Infinity
    let valid = true
    for (let j = i - len + 1; j <= i; j++) {
      const h = bars[j].high
      const l = bars[j].low
      if (!Number.isFinite(h) || !Number.isFinite(l)) { valid = false; break }
      if (h > hi) hi = h
      if (l < lo) lo = l
    }
    if (!valid) continue

    const targetIndex = i + off
    if (targetIndex < 0 || targetIndex >= bars.length) continue
    const t = bars[targetIndex].time
    if (!Number.isFinite(t)) continue
    out.push({ time: t, upper: hi, basis: (hi + lo) / 2, lower: lo })
  }
  return out
}
